import React from "react";
import {
  Grid,
  Stack,
  Box,
  Typography,
  Chip,
  Button,
  Paper,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { styled } from "@mui/material/styles";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  background: "linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)",
  border: `1px solid ${theme.palette.grey[100]}`,
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
    transform: "translateY(-4px)",
  },
}));

const InfoCard = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #FFFFFF 0%, #F9FBFD 100%)",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[100]}`,
  minWidth: "160px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 40,
  height: 40,
  borderRadius: "50%",
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  borderRadius: theme.spacing(1),
  height: 28,
  backgroundColor: theme.palette.primary.light,
  color: theme.palette.primary.dark,
  border: "none",
  "& .MuiChip-label": {
    padding: theme.spacing(0, 2),
  },
}));

const StatusBadge = styled(Box)(({ theme, status }) => ({
  padding: theme.spacing(0.5, 1.5),
  borderRadius: theme.spacing(3),
  fontSize: "0.7rem",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  marginLeft: theme.spacing(1),
  backgroundColor:
    status === "expired"
      ? theme.palette.error.light
      : theme.palette.success.light,
  color:
    status === "expired"
      ? theme.palette.error.dark
      : theme.palette.success.dark,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1, 3),
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  background: "linear-gradient(135deg, #1976d2 0%, #115293 100%)",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
    background: "linear-gradient(135deg, #115293 0%, #0d3c6e 100%)",
    transform: "translateY(-2px)",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
    transition: "left 0.5s",
  },
  "&:hover::after": {
    left: "100%",
  },
}));

const PlanDisplay = ({ data, id }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExpired =
    data?.expiryDate && dayjs(data.expiryDate).isBefore(dayjs());
  const daysUntilExpiry = data?.expiryDate
    ? dayjs(data.expiryDate).diff(dayjs(), "day")
    : null;

  return (
    <CardContent sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={3}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          <InfoCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconWrapper>
                <AssignmentIcon fontSize="small" />
              </IconWrapper>
              <Typography
                variant="overline"
                color="textSecondary"
                fontWeight={600}
              >
                Current Plan
              </Typography>
            </Box>
            {data?.subscriptionPlan ? (
              <StyledChip label={data.subscriptionPlan} size="small" />
            ) : (
              <Typography variant="h6" color="textSecondary" sx={{ mt: 0.5 }}>
                No active plan
              </Typography>
            )}
          </InfoCard>

          <InfoCard>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconWrapper>
                <CalendarTodayIcon fontSize="small" />
              </IconWrapper>
              <Typography
                variant="overline"
                color="textSecondary"
                fontWeight={600}
              >
                Expiry Date
              </Typography>
            </Box>
            {data?.expiryDate && dayjs(data.expiryDate).isValid() ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="h6" fontWeight={600} sx={{ mr: 1 }}>
                  {dayjs(data.expiryDate).format("DD MMM YYYY")}
                </Typography>
                {isExpired ? (
                  <StatusBadge status="expired">Expired</StatusBadge>
                ) : daysUntilExpiry <= 7 ? (
                  <StatusBadge status="warning">
                    {daysUntilExpiry === 0
                      ? "Today"
                      : `${daysUntilExpiry} day${
                          daysUntilExpiry === 1 ? "" : "s"
                        } left`}
                  </StatusBadge>
                ) : null}
              </Box>
            ) : (
              <Typography variant="h6" color="textSecondary">
                No expiry date
              </Typography>
            )}
          </InfoCard>
        </Stack>

        <Link to={`/home/${id}/add-plan`} style={{ textDecoration: "none" }}>
          <ActionButton
            size="medium"
            variant="contained"
            endIcon={<ArrowForwardIosIcon sx={{ fontSize: "14px" }} />}
            fullWidth={isMobile}
          >
            {data?.subscriptionPlan ? "Manage Plan" : "Get Started"}
          </ActionButton>
        </Link>
      </Stack>
    </CardContent>
  );
};

export default PlanDisplay;
